import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { Badge } from './Badge';
import type { BadgeTone } from './Badge';

/**
 * What a person is on this screen: what they may do here, not what they are called.
 *
 * `superuser` is the platform account that can act as any organization, so it
 * outranks a membership and is shown instead of one.
 */
export type AccountRole = 'superuser' | 'admin' | 'member';

/** The label of each role. Literals, so the locale test can see them. */
export const ROLE_KEYS: Record<AccountRole, string> = {
  superuser: 'roles.superuser',
  admin: 'roles.admin',
  member: 'roles.member',
};

export const ROLE_BADGES: Record<AccountRole, { icon: string; tone: BadgeTone }> = {
  // Loud on purpose: a superuser is reading somebody else's centre.
  superuser: { icon: 'heroicons:shield-check', tone: 'danger' },
  admin: { icon: 'heroicons:key', tone: 'accent' },
  member: { icon: 'heroicons:user', tone: 'neutral' },
};

/** The role to show, given the account and the seat it holds in the active organization. */
export function accountRole(isSuperuser: boolean, role: 'admin' | 'member' | null): AccountRole {
  if (isSuperuser) return 'superuser';
  return role === 'admin' ? 'admin' : 'member';
}

export interface RoleBadgeProps {
  role: AccountRole;
  className?: string;
}

/** What the person signed in may do here, as a pill. */
export function RoleBadge({ role, className = '' }: RoleBadgeProps) {
  const { t } = useTranslation();
  const style = ROLE_BADGES[role];
  return (
    <Badge tone={style.tone} className={className}>
      <Icon icon={style.icon} aria-hidden="true" className="size-3.5 shrink-0" />
      {t(ROLE_KEYS[role])}
    </Badge>
  );
}
